<template>
    <div
        ref="rootRef"
        class="
            flex
            items-center
            gap-4
            py-3
            px-5
            rounded-lg
            border-solid border-2 border-transparent
        "
        :class="{
            'bg-white border-primary shadow-md': isOpen,
            'bg-gray-100': !isOpen,
        }"
        @click="isOpen = !isOpen"
        v-bind="$attrs"
    >
        <div
            v-if="!selectedValue"
            :class="{
                'text-gray-400': !isOpen,
                'text-gray-700': isOpen,
            }"
        >
            {{ placeholder }}
        </div>
        <div v-else class="text-gray-800">
            {{ selectedValue?.label }}
        </div>
        <ion-icon
            :name="isOpen ? 'chevron-up-outline' : 'chevron-down-outline'"
            class="text-xl ml-auto"
        ></ion-icon>
        <input type="hidden" :name="name" :value="value" />
    </div>
    <teleport to="body">
        <div
            v-show="isOpen"
            class="fixed inset-0 z-[1000] bg-white bg-opacity-10"
            @click="isOpen = false"
        ></div>
        <div
            v-show="isOpen"
            ref="optionListRef"
            class="z-[2000] bg-white shadow-2xl rounded-md px-2 text-gray-700"
        >
            <div class="my-2">
                <input
                    @input="pointer = 0"
                    v-model="search"
                    type="text"
                    ref="searchInputRef"
                    placeholder="Search"
                    class="w-full bg-gray-100 rounded-lg p-2 outline-none"
                />
            </div>
            <ul class="max-h-[200px] overflow-y-auto">
                <li
                    :data-key="option.value"
                    v-for="(option, index) in filteredOptions"
                    class="
                        mb-2
                        px-5
                        py-2
                        hover:bg-gray-100
                        rounded-md
                        cursor-pointer
                        select-none
                    "
                    @click="select(option, index)"
                    :class="{
                        'bg-primary bg-opacity-20': pointer === index,
                    }"
                >
                    {{ option.label }}
                </li>
            </ul>
        </div>
    </teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { createPopper } from "@popperjs/core";

const props = defineProps({
    name: String,
    options: Array,
    placeholder: String,
    modelValue: {
        type: String,
        required: false,
        default: null,
    },
});

const filteredOptions = computed(() => {
    return props.options.filter((option) => {
        const regex = new RegExp(search.value, "i");
        return regex.test(option.label);
    });
});

const rootRef = ref(null);
const optionListRef = ref(null);
const searchInputRef = ref(null);

const value = computed({
    get() {
        return props.modelValue;
    },
    set(value) {
        emit("update:modelValue", value);
    },
});

const isOpen = ref(false);
const popperInstance = ref(null);
const pointer = ref(0);
const search = ref("");

const selectedValue = computed(() => {
    return props.options.find((option) => option.value === value.value);
});

const emit = defineEmits(["update:modelValue"]);

function handleKeyMovement(e) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") {
        return;
    }
    if (e.key === "ArrowDown") {
        e.preventDefault();
        pointer.value = pointer.value + 1;
    } else if (e.key === "ArrowUp") {
        e.preventDefault();
        pointer.value = pointer.value - 1;
    } else if (e.key === "Enter") {
        e.preventDefault();
        value.value = filteredOptions.value[pointer.value].value;
        isOpen.value = false;

        emit("update:modelValue", value.value);
    }
    if (pointer.value > filteredOptions.value.length - 1) {
        pointer.value = 0;
    } else if (pointer.value < 0) {
        pointer.value = filteredOptions.value.length - 1;
    }
    // scroll to selected element
    const selectedElement = optionListRef.value.querySelector(
        `[data-key="${filteredOptions.value[pointer.value].value}"]`
    );
    selectedElement.scrollIntoView({
        block: "nearest",
    });
}

function select(option, index) {
    value.value = option.value;
    isOpen.value = false;
    pointer.value = index;
}

watch(isOpen, (newValue) => {
    if (newValue) {
        nextTick(() => {
            popperInstance.value = createPopper(
                rootRef.value,
                optionListRef.value,
                {
                    placement: "bottom",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 8],
                            },
                        },
                        {
                            name: "sameWidth",
                            enabled: true,
                            phase: "beforeWrite",
                            requires: ["computeStyles"],
                            fn({ state }) {
                                state.styles.popper.width = `${state.rects.reference.width}px`;
                            },
                            effect({ state }) {
                                state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                            },
                        },
                    ],
                }
            );
            popperInstance.value.forceUpdate();
            searchInputRef.value.focus();
        });
        // handle keydown events
        window.addEventListener("keydown", handleKeyMovement);
    } else {
        popperInstance.value.destroy();
        window.removeEventListener("keydown", handleKeyMovement);
    }
});
</script>

<style scoped></style>
